// Mirrors GPMetals.Core.Helpers.PartNumberRules: what counts as a real part number. The API's eBay
// filler is "WILM" plus five digits (older data holds it in the part number itself) and staff type
// "Not Available" to say there isn't one - neither is a number worth searching or pricing by.

export const GENERATED_PART_NUMBER_PREFIX = "WILM";

const PLACEHOLDERS = new Set(["not available"]);

export function isGeneratedPartNumber(value: string | null | undefined): boolean {
  return !!value && value.trim().toUpperCase().startsWith(GENERATED_PART_NUMBER_PREFIX);
}

export function isRealPartNumber(value: string | null | undefined): boolean {
  if (!value) return false;
  const cleaned = value.trim().replace(/,$/, "").trim();
  if (cleaned.length === 0 || PLACEHOLDERS.has(cleaned.toLowerCase())) return false;
  return !isGeneratedPartNumber(cleaned);
}

/** The eBay Optimisation "no part numbers" test: neither the MPN nor the OE number is real. */
export function hasRealPartNumber(partNumber: string | null | undefined, oePartNumber: string | null | undefined): boolean {
  return isRealPartNumber(partNumber) || isRealPartNumber(oePartNumber);
}

/**
 * The custom aspect that carries any further numbers a part answers to (superseded numbers, the OE
 * number when the MPN is the part's own). Stored as one comma-separated string on the part aspect and
 * sent to eBay as an item specific - PartNumberRules.OtherPartNumbersAspect on the API.
 */
export const OTHER_PART_NUMBERS_ASPECT = "Other Part Numbers";

const sameNumber = (a: string, b: string) => a.toUpperCase() === b.toUpperCase();

/** The stored aspect value as a list: split on commas, semicolons or newlines, trimmed, no blanks, no repeats. */
export function parseOtherPartNumbers(value: string | null | undefined): string[] {
  if (!value) return [];
  const numbers: string[] = [];
  for (const raw of value.split(/[,;\n]/)) {
    const cleaned = raw.trim();
    if (!isRealPartNumber(cleaned)) continue;
    if (numbers.some((existing) => sameNumber(existing, cleaned))) continue;
    numbers.push(cleaned);
  }
  return numbers;
}

export function joinOtherPartNumbers(numbers: string[]): string {
  return numbers.join(", ");
}

/**
 * The list with another number added. Nothing changes when the number is not real, is already in the
 * list, or is one of the part's primary numbers (`exclude` - the MPN and OE number, which the listing
 * already carries in their own specifics).
 */
export function addOtherPartNumber(numbers: string[], value: string, exclude: Array<string | null | undefined> = []): string[] {
  const cleaned = value.trim().replace(/,$/, "").trim();
  if (!isRealPartNumber(cleaned)) return numbers;
  if (numbers.some((existing) => sameNumber(existing, cleaned))) return numbers;
  if (exclude.some((primary) => primary && sameNumber(primary.trim(), cleaned))) return numbers;
  return [...numbers, cleaned];
}

export function removeOtherPartNumber(numbers: string[], value: string): string[] {
  return numbers.filter((existing) => !sameNumber(existing, value));
}
